import { useState } from 'react';
import i18n from '../i18n/i18n';

// Helpers
import { getInternByInfo, createIntern, updateIntern } from '../helpers/api/callIntern';
import { addTraineesSession, deleteTraineesSession } from '../helpers/api/callSession';
import { getSocietyById } from '../helpers/api/callSociety';

// Types
import { useSessionStore } from '../store/SessionStore';
import { InternData } from '../types/internData';

const useTrainee = (sessionId: string, traineesId: string[], updatingTrainee: InternData) => {
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { session, setSession } = useSessionStore();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const openDrawer = () => setIsDrawerOpen(true);
    const closeDrawer = () => setIsDrawerOpen(false);

    const handleCreateOrAddTrainee = async (data: InternData) => {
        setIsLoading(true);
        setError(null);

        try {
            // Check if the intern already exists
            const intern = await getInternByInfo(data.firstname as string, data.lastname as string, data.societyId as string);

            let internId: string | null = null;

            if (!intern) {
                const response = await createIntern(
                    data.civility as string,
                    data.firstname as string,
                    data.lastname as string,
                    (data.societyId ?? '').toString(),
                    data.email as string,
                    data.registrationNumber as string
                );

                if (response.status !== 201 || !response.id) {
                    setError(i18n.t('AddIntern.errors.createIntern'));
                    setIsLoading(false);
                    return;
                }

                internId = response.id;
            } else {
                internId = intern.id;
            }

            // Check if the intern is already added to the session
            if (traineesId.includes(internId)) {
                setError(i18n.t('AddIntern.errors.alreadyAdded'));
                setIsLoading(false);
                return;
            }

            // Add the intern to the session
            const responseUpdateSession = await addTraineesSession(sessionId, [internId.toString()]);

            if (responseUpdateSession.status === 403) {
                setError(i18n.t('AddIntern.errors.existingEvaluations'));
                setIsLoading(false);
                return;
            }

            if (responseUpdateSession.status === 405) {
                setError(i18n.t('AddIntern.errors.updateSession'));
                setIsLoading(false);
                return;
            }

            setSession({ ...session, trainees: [...traineesId, internId] });
            closeDrawer();
            setIsLoading(false);
        } catch (error) {
            console.error(error);
            setError(i18n.t('AddIntern.errors.unexpected'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditTrainee = async (data: InternData) => {
        setIsLoading(true);
        const society = await getSocietyById(data.societyId as string);

        const response = await updateIntern(
            updatingTrainee.id,
            data.civility as string,
            data.firstname as string,
            data.lastname as string,
            society?.id?.toString() as string,
            data.email as string,
            data.registrationNumber as string
        );

        if (response !== 204) {
            setError(i18n.t('AddIntern.errors.updateIntern'));
            setIsLoading(false);
            return;
        }

        const updatedIntern: InternData = {
            ...updatingTrainee,
            civility: data.civility,
            firstname: data.firstname as string,
            lastname: data.lastname as string,
            email: data.email as string,
            registrationNumber: data.registrationNumber,
            societyId: society?.id ?? updatingTrainee.societyId
        };

        const updatedTrainees = session.trainees.map((traineeId) => (traineeId === updatingTrainee.id ? updatedIntern.id : traineeId));

        setSession({ ...session, trainees: updatedTrainees });
        closeDrawer();
        setIsLoading(false);
        return;
    };

    const handleDeleteTrainee = async (traineeId: string) => {
        setIsLoading(true);
        const response = await deleteTraineesSession(sessionId, [traineeId.toString()]);

        if (response.status === 204) {
            const updatedTrainees = session.trainees.filter((traineeId) => traineeId !== traineeId);
            setSession({ ...session, trainees: updatedTrainees });
            closeDrawer();
        } else {
            setError(i18n.t('AddIntern.errors.deleteIntern'));
        }
        setIsLoading(false);
    };

    return {
        isDrawerOpen,
        openDrawer,
        closeDrawer,
        error,
        setError,
        isLoading,
        handleCreateOrAddTrainee,
        handleEditTrainee,
        handleDeleteTrainee
    };
};

export default useTrainee;
